import { IMapActivity, IMapCostCenter, IMapAccount, IMapLink } from './activitymap.models';
const API_BASE = '/api/activitymap';

interface IApiEnvelope<T> {
  ok: boolean;
  message?: string;
  data: T;
  errors?: Array<{
    field?: string;
    code?: string;
    message: string;
  }>;
  warnings?: Array<{
    field?: string;
    code?: string;
    message: string;
  }>;
}

function readItems<T>(payload: IApiEnvelope<any>): T[] {
  if (!payload.ok) {
    throw new Error(payload.message || 'Request failed');
  }

  if (Array.isArray(payload.data)) {
    return payload.data as T[];
  }

  if (Array.isArray(payload.data?.items)) {
    return payload.data.items as T[];
  }

  return [];
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      searchParams.append(key, `${value}`);
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(`/biz${url}`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

async function sendJson<T>(
  url: string,
  method: 'POST' | 'PUT',
  body: any
): Promise<T> {
  const response = await fetch(`/biz${url}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

async function readJson<T>(response: Response): Promise<T> { 
  if (!response.ok) throw new Error(await response.text() || `HTTP ${response.status}`); 
  const payload = await response.json(); 
  return payload.data as T; 
}

export async function fetchCostCenters(): Promise<IMapCostCenter[]> {
  const payload = await getJson<IApiEnvelope<any>>(`${API_BASE}/costcenters`);
  return readItems<IMapCostCenter>(payload);
}

export async function fetchActivities(
  costcenterid: string,
  search = ''
): Promise<IMapActivity[]> {
  const query = buildQuery({ costcenterid, search });
  const payload = await getJson<IApiEnvelope<any>>(`${API_BASE}/activities${query}`);
  return readItems<IMapActivity>(payload);
}

export async function fetchAccounts(
  search = '',
  sixdigit = true
): Promise<IMapAccount[]> {
  const query = buildQuery({ search, sixdigit: sixdigit ? 'true' : 'false' });
  const payload = await getJson<IApiEnvelope<any>>(`${API_BASE}/accounts${query}`);
  return readItems<IMapAccount>(payload);
}

export async function fetchLinks(costcenterid: string): Promise<IMapLink[]> { 
  const query = buildQuery({ costcenterid });
  const payload = await getJson<IApiEnvelope<any>>(`${API_BASE}/links?${query}`);
  return readItems<IMapLink>(payload);
}

export async function fetchLinks1(costcenterid: string): Promise<IMapLink[]> { 
  const params = new URLSearchParams({ costcenterid }); 
  //return readJson(await fetch(`${API_BASE}/links?${params}`)); 
  const payload = await getJson<IApiEnvelope<any>>(
      `${API_BASE}/links?${params}`
    );
  
    if (!payload.ok) {
      throw new Error(payload.message || 'Failed to load activities');
    }
  
    return payload.data;
}

export async function fetchSummary(costcenterid?: string): Promise<any> { 
  const params = new URLSearchParams(); if (costcenterid) params.set('costcenterid', costcenterid); 
  return readJson(await fetch(`/biz${API_BASE}/summary?${params}`)); 
  // const payload = await getJson<IApiEnvelope<any>>(
  //     `${API_BASE}/summary?${params}`
  //   );
  
  //   if (!payload.ok) {
  //     throw new Error(payload.message || 'Failed to load activities');
  //   }
  
  //   return payload.data;
}

export async function processAllocation(
  payload: { 
    costcenterid: string; 
    activityids: string[]; 
    accountids: string[]; 
    mode: 'RATIO_ONLY' | 'EQUAL'; 
    totalamount?: number; 
    editorid?: string; 
  }
): Promise<any> { 
  return readJson(await fetch(`/biz${API_BASE}/process`, 
    { 
      method: 'POST', 
      headers: { 'Content-Type': 'application/json' }, 
      body: JSON.stringify(payload) 
    }
  )); 
}
